/**
 * Reader module (#92): today's main page — sidebar, landing page (the
 * empty state), document area and contradiction offcanvas. Premounted at
 * boot because BaseApp wires this DOM once; see "Boot integration".
 */

import template from './reader.template.html?raw';
import type { AppModule } from '../../shell/module.types';
import type { TranslationParams } from '../../../i18n/ui-manager.types';

export interface ReaderModuleDeps {
  getLastOpenedName: () => string | null;
  t: (key: string, params?: TranslationParams) => string;
  topbarSlot: HTMLElement;
  /** Close overlays whose chrome lives on <body> (the contradiction offcanvas). */
  closeOverlays: () => void;
  /** #140: VITE_FEATURE_COMPARE. */
  compareEnabled: boolean;
  /**
   * #140, flag on: put the Compare panel into the sidebar container on enter;
   * the returned cleanup runs on leave (stops its polling).
   */
  mountComparePanel?: (container: HTMLElement) => () => void;
  /** #140, flag on: told when the Reader is left. */
  onLeave?: () => void;
}

/**
 * #140, flag on: the sidebar holds only the Compare panel container and the
 * hint; the open buttons add a document to the collection; no stored-analysis
 * bar (no viewing without adding).
 */
function applyCompareLayout(outlet: HTMLElement): HTMLElement {
  const sidebar = outlet.querySelector<HTMLElement>('.sidebar-col')!;
  for (const section of Array.from(
    sidebar.querySelectorAll('.sidebar-section')
  )) {
    section.remove();
  }
  const container = document.createElement('div');
  container.id = 'compare-panel-container';
  container.className = 'compare-panel-container';
  sidebar.prepend(container);
  outlet
    .querySelector('#sidebar-hint')
    ?.setAttribute('data-i18n', 'compare.hint');

  const load = outlet.querySelector<HTMLElement>('#load-document');
  if (load) {
    load.setAttribute('data-i18n-title', 'compare.addDocument');
    const icon = document.createElement('span');
    icon.textContent = '＋';
    const label = document.createElement('span');
    label.setAttribute('data-i18n', 'compare.addDocument');
    load.replaceChildren(icon, label);
  }
  outlet
    .querySelector('#landing-open-btn')
    ?.setAttribute('data-i18n', 'compare.addDocument');
  outlet.querySelector('[data-stored-analysis-bar]')?.remove();
  return container;
}

export function createReaderModule(deps: ReaderModuleDeps): AppModule {
  let topbarGroup: HTMLElement | null = null;
  let compareContainer: HTMLElement | null = null;
  let leaveCompare: (() => void) | null = null;

  return {
    id: 'reader',
    route: '/reader',
    labelKey: 'modules.reader.label',
    descriptionKey: 'modules.reader.description',
    icon: 'reader',
    enabled: true,
    showOnDashboard: true,

    mount(outlet) {
      outlet.innerHTML = template;
      if (deps.compareEnabled) compareContainer = applyCompareLayout(outlet);
      topbarGroup = outlet.querySelector<HTMLElement>(
        '[data-topbar-group="reader"]'
      );
      if (topbarGroup) {
        topbarGroup.hidden = true;
        deps.topbarSlot.appendChild(topbarGroup);
      }
    },

    onEnter() {
      if (topbarGroup) topbarGroup.hidden = false;
      if (compareContainer && deps.mountComparePanel && !leaveCompare) {
        leaveCompare = deps.mountComparePanel(compareContainer);
      }
    },

    onLeave() {
      if (topbarGroup) topbarGroup.hidden = true;
      leaveCompare?.();
      leaveCompare = null;
      deps.onLeave?.();
      deps.closeOverlays();
    },

    getStatus() {
      const filename = deps.getLastOpenedName();
      return filename ? deps.t('modules.reader.status', { filename }) : null;
    },
  };
}
